import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { Alert, Snackbar } from "@mui/material";
import uploadAndDownloadService from "../../services/uploadAndDownloadFile";
import UtilsFormule from "../../utils/UtilsFormule";
import AddPayement from "./AddPayement";
import RejeterPayement from "./RejeterPayement";
import RejeterDefPayement from "./RejeterDefPayement";
import { useNavigate } from "react-router-dom";

const fmt = (v) => parseFloat(Number(v || 0).toFixed(3));

function getRef(row) {
  if (row.typePayement === "Espéce") return `${row.ref}-${row.year}`;
  if (row.typePayement === "Virement") return row.numVirement || "";
  if (row.typePayement === "Chéque / Traites") return row.codeCheque || "";
  if (row.typePayement === "Prélevement") return "Prélevement";
  return row.ref || "";
}

function getEcheanceStr(row) {
  if (row.typePayement === "Espéce" || row.typePayement === "En ligne")
    return UtilsFormule.formatDate(row.creationDate) || "";
  if (row.typePayement === "Virement" || row.typePayement === "Chéque / Traites")
    return UtilsFormule.formatDate(row.echeanceDate) || "";
  return "";
}

function getVenteAchat(row) {
  if (row.facture !== null || row.bonDeCommande != null || row.charge != null || row.employe != null)
    return "Achat";
  return "Vente";
}

function getDocumentLabel(row) {
  if (row.facture) return `Facture: ${row.facture.ref}-${row.facture.title}`;
  if (row.bonDeCommande) return `BDC: ${row.bonDeCommande.ref}-${row.bonDeCommande.title}`;
  if (row.factureSale) return `Facture: ${row.factureSale.ref}-${row.factureSale.title}`;
  if (row.bonDeCommandeSale) return `BDC: ${row.bonDeCommandeSale.ref}-${row.bonDeCommandeSale.title}`;
  if (row.paymentTransfert) return row.paymentTransfert.name;
  if (row.tiersAccountTransaction) return row.tiersAccountTransaction.tiersAccount?.title || "";
  if (row.employe) return `Avance: ${row.employe.cin}-${row.employe.firstName} ${row.employe.lastName}`;
  if (row.charge) return `Charge: ${row.charge.ref}-${row.charge.title}`;
  if (row.order) return `Ticket N°${row.order.reference}`;
  return "";
}

const DataTableTransactions = ({ listTransaction }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [onAddPayement, setOnAddPayement] = useState(false);
  const [onRejeterPayement, setOnRejeterPayement] = useState(false);
  const [onRejeterDefPayement, setOnRejeterDefPayement] = useState(false);
  const [idPayement, setIdPayement] = useState();
  const [openInfoAlert, setOpenInfoAlert] = useState(false);
  const [infoAlertText, setInfoAlertText] = useState();

  const onAddPayementHandler = (id) => { setIdPayement(id); setOnAddPayement(true); };
  const onAddRejeterHandler = (id) => { setIdPayement(id); setOnRejeterPayement(true); };
  const onAddRejeterDefHandler = (id) => { setIdPayement(id); setOnRejeterDefPayement(true); };

  const filtered = useMemo(() => {
    if (!search.trim()) return listTransaction;
    const q = search.toLowerCase();
    return listTransaction.filter((r) =>
      getRef(r).toLowerCase().includes(q) ||
      (r.typePayement || "").toLowerCase().includes(q) ||
      getDocumentLabel(r).toLowerCase().includes(q) ||
      getVenteAchat(r).toLowerCase().includes(q)
    );
  }, [listTransaction, search]);

  const handleExportExcel = () => {
    if (!listTransaction || listTransaction.length === 0) return;
    const rows = listTransaction.map((row) => {
      const isAchat = getVenteAchat(row) === "Achat";
      const montant = row.amountPayed != null ? fmt(row.amountPayed) : "";
      return {
        "Ref": getRef(row),
        "Type": row.typePayement || "",
        "Libelle": getDocumentLabel(row),
        "Date Echeance": getEcheanceStr(row),
        "Credit": isAchat ? "" : montant,
        "Debit": isAchat ? montant : "",
        "Date Operation": row.operationDate != null ? UtilsFormule.formatDate(row.operationDate) : "",
        "Date Valeur": row.valeurDate != null ? UtilsFormule.formatDate(row.valeurDate) : "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "transactions.xlsx");
  };

  const onShowFileHandler = (path) => {
    uploadAndDownloadService.downloadFile(path).then((response) => {
      const type = response.headers["content-type"];
      const file = new Blob([response.data], { type });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    });
  };

  const renderExpandContent = (row) => {
    const type = row.typePayement;
    const validation = row.typePayementValidation;

    if (type === "Espéce" || type === "En ligne") return renderEspeceEnLigneExpand(row, type, validation);
    if (type === "Virement") return renderVirementExpand(row, validation);
    if (type === "Chéque / Traites") return renderChequeExpand(row, validation);
    if (type === "Prélevement") return renderPrelevementExpand(row, validation);
    return null;
  };

  const actionButtons = (row) => (
    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
      <button className="eb-action-btn" title="Payer"
        data-toggle="modal" data-target="#add-payement"
        onClick={() => onAddPayementHandler(row.idPayementInfo)}>
        <i className="fas fa-check" style={{ color: "#1b5e20" }}></i>
      </button>
      <button className="eb-action-btn" title="Rejeter"
        data-toggle="modal" data-target="#rejeter-payement"
        onClick={() => onAddRejeterHandler(row.idPayementInfo)}>
        <i className="fas fa-times" style={{ color: "#e65100" }}></i>
      </button>
      <button className="eb-action-btn" title="Rejeter définitivement"
        data-toggle="modal" data-target="#rejeter-payement"
        onClick={() => onAddRejeterDefHandler(row.idPayementInfo)}>
        <i className="fas fa-ban" style={{ color: "#b71c1c" }}></i>
      </button>
    </div>
  );

  const renderEspeceEnLigneExpand = (row, type, validation) => {
    if (validation === "accept") return (
      <div style={{ padding: "12px 20px", background: "#f0fff4", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-accept">Accepté</span>&nbsp;
        <strong>{type}</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Réf.Op: {row.refOperation} —
        Date op: {UtilsFormule.formatDateHours(row.operationDate)} — Date valeur: {UtilsFormule.formatDateHours(row.valeurDate)}
      </div>
    );
    if (validation === "refuse") return (
      <div style={{ padding: "12px 20px", background: "#fff8f0", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-refuse">Rejeté</span>&nbsp;
        <strong>{type}</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date refus: {UtilsFormule.formatDateHours(row.rejectDate)}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
    if (validation === "refuse-def") return (
      <div style={{ padding: "12px 20px", background: "#fff5f5", fontSize: "0.82rem" }}>
        <span className="eb-badge eb-badge-refusedef">Refusé définitivement</span>&nbsp;
        <strong>{type}</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date op: {UtilsFormule.formatDateHours(row.operationDate)}
      </div>
    );
    return (
      <div style={{ padding: "12px 20px", background: "#f9f9f9", fontSize: "0.82rem" }}>
        <strong>{type}</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
  };

  const renderVirementExpand = (row, validation) => {
    if (validation === "accept") return (
      <div style={{ padding: "12px 20px", background: "#f0fff4", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-accept">Accepté</span>&nbsp;
        <strong>Virement</strong> — N°: {row.numVirement} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Réf.Op: {row.refOperation} —
        Date op: {UtilsFormule.formatDateHours(row.operationDate)} — Date valeur: {UtilsFormule.formatDateHours(row.valeurDate)}
      </div>
    );
    if (validation === "refuse") return (
      <div style={{ padding: "12px 20px", background: "#fff8f0", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-refuse">Rejeté</span>&nbsp;
        <strong>Virement</strong> — N°: {row.numVirement} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date refus: {UtilsFormule.formatDateHours(row.rejectDate)}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
    if (validation === "refuse-def") return (
      <div style={{ padding: "12px 20px", background: "#fff5f5", fontSize: "0.82rem" }}>
        <span className="eb-badge eb-badge-refusedef">Refusé définitivement</span>&nbsp;
        <strong>Virement</strong> — N°: {row.numVirement} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date op: {UtilsFormule.formatDateHours(row.operationDate)}
      </div>
    );
    return (
      <div style={{ padding: "12px 20px", background: "#f9f9f9", fontSize: "0.82rem" }}>
        <strong>Virement</strong> — N°: {row.numVirement} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
  };

  const renderChequeExpand = (row, validation) => {
    if (validation === "accept") return (
      <div style={{ padding: "12px 20px", background: "#f0fff4", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-accept">Accepté</span>&nbsp;
        <strong>Chèque</strong> — Code: {row.codeCheque} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Réf.Op: {row.refOperation} —
        Date op: {UtilsFormule.formatDateHours(row.operationDate)} — Date valeur: {UtilsFormule.formatDateHours(row.valeurDate)}
      </div>
    );
    if (validation === "refuse") return (
      <div style={{ padding: "12px 20px", background: "#fff8f0", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-refuse">Rejeté</span>&nbsp;
        <strong>Chèque</strong> — Code: {row.codeCheque} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date refus: {UtilsFormule.formatDateHours(row.rejectDate)}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
    if (validation === "refuse-def") return (
      <div style={{ padding: "12px 20px", background: "#fff5f5", fontSize: "0.82rem" }}>
        <span className="eb-badge eb-badge-refusedef">Refusé définitivement</span>&nbsp;
        <strong>Chèque</strong> — Code: {row.codeCheque} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date op: {UtilsFormule.formatDateHours(row.operationDate)}
      </div>
    );
    return (
      <div style={{ padding: "12px 20px", background: "#f9f9f9", fontSize: "0.82rem" }}>
        <strong>Chèque</strong> — Code: {row.codeCheque} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
  };

  const renderPrelevementExpand = (row, validation) => {
    if (validation === "accept") return (
      <div style={{ padding: "12px 20px", background: "#f0fff4", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-accept">Accepté</span>&nbsp;
        <strong>Prélevement</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Réf.Op: {row.refOperation} —
        Date op: {UtilsFormule.formatDateHours(row.operationDate)} — Date valeur: {UtilsFormule.formatDateHours(row.valeurDate)}
      </div>
    );
    if (validation === "refuse") return (
      <div style={{ padding: "12px 20px", background: "#fff8f0", fontSize: "0.82rem" }}>
        {row.descriptionOperation && <p style={{ marginBottom: 6 }}>{row.descriptionOperation}</p>}
        <span className="eb-badge eb-badge-refuse">Rejeté</span>&nbsp;
        <strong>Prélevement</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date refus: {UtilsFormule.formatDateHours(row.rejectDate)}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
    if (validation === "refuse-def") return (
      <div style={{ padding: "12px 20px", background: "#fff5f5", fontSize: "0.82rem" }}>
        <span className="eb-badge eb-badge-refusedef">Refusé définitivement</span>&nbsp;
        <strong>Prélevement</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed} — Date op: {UtilsFormule.formatDateHours(row.operationDate)}
      </div>
    );
    return (
      <div style={{ padding: "12px 20px", background: "#f9f9f9", fontSize: "0.82rem" }}>
        <strong>Prélevement</strong> — Réf: {row.ref}-{row.year} — DC: {UtilsFormule.formatDateHours(row.creationDate)} —
        M.Payé: {row.amountPayed}
        <div style={{ marginTop: 8 }}>{actionButtons(row)}</div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .eb-wrap { font-family: 'Source Sans Pro', Arial, sans-serif; margin-top: 16px; }
        .eb-header {
          background: #111; color: #fff; border-radius: 4px 4px 0 0;
          padding: 18px 24px 14px; display: flex; align-items: center;
          justify-content: space-between; flex-wrap: wrap; gap: 10px;
        }
        .eb-header-title { font-size: 1.1rem; font-weight: 700; letter-spacing: 1px; }
        .eb-header-sub   { font-size: 0.8rem; color: #aaa; margin-top: 2px; }
        .eb-toolbar {
          background: #fff; border: 1px solid #e0e0e0; border-top: none;
          padding: 10px 24px; display: flex; align-items: center;
          justify-content: space-between; gap: 10px; flex-wrap: wrap;
        }
        .eb-search {
          border: 1.5px solid #ccc; border-radius: 3px; padding: 6px 12px;
          font-size: 0.85rem; outline: none; width: 220px;
        }
        .eb-search:focus { border-color: #111; }
        .eb-table-wrap {
          border: 1px solid #e0e0e0; border-top: none;
          border-radius: 0 0 4px 4px; overflow: hidden;
        }
        .eb-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .eb-table thead tr { background: #222; color: #fff; }
        .eb-table thead th {
          padding: 10px 14px; font-weight: 600; letter-spacing: 0.5px;
          font-size: 0.8rem; white-space: nowrap;
        }
        .eb-table tbody tr { border-bottom: 1px solid #f0f0f0; transition: background 0.15s; cursor: pointer; }
        .eb-table tbody tr:hover { background: #f5f5f5 !important; }
        .eb-table tbody td { padding: 9px 14px; vertical-align: middle; }
        .eb-row-vente { background: #f0fff4; }
        .eb-row-achat { background: #fffde7; }
        .eb-expand-row td { padding: 0 !important; background: #fafafa; cursor: default; }
        .eb-badge {
          display: inline-block; padding: 2px 8px; border-radius: 12px;
          font-size: 0.72rem; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;
        }
        .eb-badge-vente   { background: #c8e6c9; color: #1b5e20; }
        .eb-badge-achat   { background: #fff9c4; color: #f57f17; }
        .eb-badge-accept  { background: #c8e6c9; color: #1b5e20; }
        .eb-badge-refuse  { background: #ffe0b2; color: #e65100; }
        .eb-badge-refusedef { background: #ffcdd2; color: #b71c1c; }
        .eb-amount { font-weight: 700; }
        .eb-action-btn {
          background: none; border: 1px solid #ccc; border-radius: 3px;
          padding: 3px 8px; cursor: pointer; font-size: 0.78rem; color: #555;
          transition: background 0.15s;
        }
        .eb-action-btn:hover { background: #eee; }
        .eb-empty { text-align: center; padding: 40px; color: #999; font-size: 0.9rem; }
        .eb-count { font-size: 0.8rem; color: #888; }
        .eb-expand-toggle { color: #888; font-size: 0.75rem; }
      `}</style>

      <div className="eb-wrap">
        {/* Header */}
        <div className="eb-header">
          <div>
            <div className="eb-header-title">
              <i className="fas fa-list mr-2"></i>
              Les transactions
            </div>
            <div className="eb-header-sub">
              {listTransaction.length} transaction{listTransaction.length > 1 ? "s" : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn btn-sm btn-outline-light" onClick={handleExportExcel}>
              <i className="fas fa-file-excel mr-1"></i> Télécharger Excel
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="eb-toolbar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fas fa-search" style={{ color: "#aaa" }}></i>
            <input
              className="eb-search"
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span className="eb-count">{filtered.length} / {listTransaction.length} entrées</span>
        </div>

        {/* Table */}
        <div className="eb-table-wrap">
          <table className="eb-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th style={{ width: "8%" }}>Op.</th>
                <th style={{ width: "14%" }}>Réf</th>
                <th style={{ width: "12%" }}>Type</th>
                <th style={{ width: "11%" }}>Echéance</th>
                <th style={{ width: "10%", textAlign: "right" }}>M.Payé</th>
                <th style={{ width: "10%", textAlign: "right" }}>TTC</th>
                <th style={{ width: "25%" }}>Document</th>
                <th style={{ width: "8%", textAlign: "center" }}>Fichier</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="eb-empty">
                    <i className="fas fa-inbox mr-2"></i>
                    Aucune transaction trouvée.
                  </td>
                </tr>
              )}
              {filtered.map((row, i) => {
                const isAchat = getVenteAchat(row) === "Achat";
                const isExpanded = expandedRow === row.idPayementInfo;
                return (
                  <React.Fragment key={row.idPayementInfo || i}>
                    <tr
                      className={isAchat ? "eb-row-achat" : "eb-row-vente"}
                      onClick={() => setExpandedRow(isExpanded ? null : row.idPayementInfo)}
                    >
                      <td style={{ textAlign: "center", color: "#999", fontSize: "0.7rem" }}>
                        {isExpanded ? "▲" : "▼"}
                      </td>
                      <td>
                        <span className={`eb-badge ${isAchat ? "eb-badge-achat" : "eb-badge-vente"}`}>
                          {isAchat ? "Achat" : "Vente"}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, fontSize: "0.82rem" }}>{getRef(row)}</td>
                      <td style={{ fontSize: "0.82rem" }}>{row.typePayement}</td>
                      <td style={{ fontSize: "0.82rem", color: "#555" }}>{getEcheanceStr(row)}</td>
                      <td style={{ textAlign: "right" }} className="eb-amount">
                        {row.amountPayed != null ? fmt(row.amountPayed) : ""}
                      </td>
                      <td style={{ textAlign: "right" }} className="eb-amount">
                        {row.allToPay != null ? fmt(row.allToPay) : ""}
                      </td>
                      <td style={{ fontSize: "0.8rem", color: "#444" }}>{getDocumentLabel(row)}</td>
                      <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                        {row.urlFile && row.urlFile !== "any" && (
                          <button
                            className="eb-action-btn"
                            title="Voir fichier"
                            onClick={() => onShowFileHandler(row.urlFile)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="eb-expand-row">
                        <td colSpan={9}>{renderExpandContent(row)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openInfoAlert && (
        <Snackbar
          open={openInfoAlert}
          autoHideDuration={2000}
          onClose={() => setOpenInfoAlert(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert variant="filled" onClose={() => setOpenInfoAlert(false)} severity="info">
            {infoAlertText}
          </Alert>
        </Snackbar>
      )}
      {onAddPayement && <AddPayement idPayementInfo={idPayement} setOnAddPayement={setOnAddPayement} />}
      {onRejeterPayement && <RejeterPayement idPayementInfo={idPayement} setOnRejeterPayement={setOnRejeterPayement} />}
      {onRejeterDefPayement && <RejeterDefPayement idPayementInfo={idPayement} setOnRejeterDefPayement={setOnRejeterDefPayement} />}
    </>
  );
};

export default DataTableTransactions;
